import React from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { components } from "../../types/api";

// Import types from API schema
type StandardizedCampaign = components["schemas"]["StandardizedCampaign"];
type CompanyData = components["schemas"]["CompanyData"];

// Define the type for API response
type ApiResponseUserCampaigns =
  components["schemas"]["ApiResponse_UserCampaignsResponse_"];

// Define the type for the assigned campaign with additional properties
interface AssignedCampaign extends StandardizedCampaign {
  margin?: number;
  startDate: string;
  endDate: string;
  goal?: components["schemas"]["_36_Enums.CampaignGoal"];
  currency?: string;
  timezone?: string;
}

interface UserCampaignsTableProps {
  selectedCompany: string | null;
  isLoadingUserCampaigns: boolean;
  userCampaignsData: ApiResponseUserCampaigns | undefined;
  userCampaignsError?: unknown;
  assignedCampaigns: AssignedCampaign[];
  handleAddCampaign: (campaign: StandardizedCampaign) => void;
  handleRemoveCampaign: (campaignId: string) => void;
}

const UserCampaignsTable: React.FC<UserCampaignsTableProps> = ({
  selectedCompany,
  isLoadingUserCampaigns,
  userCampaignsData,
  userCampaignsError,
  assignedCampaigns,
  handleAddCampaign,
  handleRemoveCampaign,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!selectedCompany) {
    return (
      <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
        <Typography
          sx={{
            fontWeight: 600,
            color: theme.palette.grey[800],
          }}
        >
          {t("campaigns.selectCompanyToViewCampaigns") ||
            "Please select a company to view available campaigns"}
        </Typography>
      </Box>
    );
  }

  // Renders a single selectable row. Ad sets are indented and tagged so they
  // read as children of their parent campaign.
  const renderCampaignRow = (
    campaign: StandardizedCampaign,
    isAdSet: boolean
  ) => {
    const isAssigned = assignedCampaigns.some((c) => c.id === campaign.id);
    return (
      <Box
        key={campaign.id}
        sx={{
          alignSelf: "stretch",
          paddingLeft: isAdSet ? 4 : 1,
          paddingRight: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 2,
          display: "flex",
          pr: 2, // Add right padding to match header
        }}
      >
        <Box
          sx={{
            flex: "1 1 0",
            height: 59,
            padding: 1.5,
            borderRadius: 1,
            overflow: "hidden",
            justifyContent: "space-between",
            alignItems: "center",
            display: "flex",
          }}
        >
          <Box
            sx={{
              flex: "1 1 0",
              height: 35,
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 1.5,
              display: "flex",
            }}
          >
            <Box
              sx={{
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                display: "flex",
              }}
            >
              <Box
                sx={{
                  color: theme.palette.text.primary,
                  fontSize: 14,
                  fontWeight: "700",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {campaign.name}
                {isAdSet && (
                  <Chip
                    size="small"
                    label={t("campaigns.adSet") || "Ad set"}
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: 150,
            display: "flex",
            alignItems: "center",
            color: theme.palette.text.primary,
            fontSize: 14,
            fontWeight: "700",
            paddingLeft: 0.5,
          }}
        >
          {campaign.originalPlatform &&
          campaign.originalPlatform !== campaign.platform
            ? `${campaign.platform} (${campaign.originalPlatform})`
            : campaign.platform}
        </Box>
        <Box
          sx={{
            width: 120, // Increased width to match header
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              height: 32,
              paddingLeft: 2,
              paddingRight: 2,
              paddingTop: 1,
              paddingBottom: 1,
              boxShadow: "0px 8px 8px rgba(0, 0, 0, 0.04)",
              borderRadius: 6.25,
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              display: "flex",
              cursor: "pointer",
            }}
            onClick={() =>
              isAssigned
                ? handleRemoveCampaign(campaign.id)
                : handleAddCampaign(campaign)
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {isAssigned ? (
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      display: "block",
                      verticalAlign: "middle",
                    }}
                  >
                    <path
                      d="M3.83301 9.83337L6.16634 12.1667L13.1663 4.83337"
                      stroke="#368548"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{
                      display: "block",
                      verticalAlign: "middle",
                    }}
                  >
                    <path
                      d="M8.5 3.16663V13.8333"
                      stroke={theme.palette.text.primary}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3.16699 8.5H13.8337"
                      stroke={theme.palette.text.primary}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </Box>
              <Box
                component="span"
                sx={{
                  color: isAssigned
                    ? theme.palette.success.main
                    : theme.palette.text.primary,
                  fontSize: 14,
                  fontWeight: "700",
                  lineHeight: "17px",
                  display: "inline-flex",
                  alignItems: "center",
                }}
              >
                {isAssigned ? "Asignada" : "Agregar"}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // Groups a company's entries so Spotify ad sets render nested under their
  // parent campaign (via parentCampaignId). Orphan ad sets are shown at root.
  const renderCompanyRows = (company: CompanyData) => {
    const list = company.campaigns ?? [];
    const parents = list.filter((c) => c.entityType !== "AD_SET");
    const adSets = list.filter((c) => c.entityType === "AD_SET");
    const parentIds = new Set(parents.map((p) => p.id));
    const adSetsByParent = adSets.reduce<Record<string, StandardizedCampaign[]>>(
      (acc, adSet) => {
        const key = adSet.parentCampaignId ?? "";
        (acc[key] = acc[key] ?? []).push(adSet);
        return acc;
      },
      {}
    );

    const rows: React.ReactNode[] = [];
    parents.forEach((parent) => {
      rows.push(renderCampaignRow(parent, false));
      (adSetsByParent[parent.id] ?? []).forEach((adSet) =>
        rows.push(renderCampaignRow(adSet, true))
      );
    });
    // Ad sets whose parent campaign isn't in the list
    adSets
      .filter(
        (a) => !a.parentCampaignId || !parentIds.has(a.parentCampaignId)
      )
      .forEach((adSet) => rows.push(renderCampaignRow(adSet, true)));

    return rows;
  };

  return (
    <>
      <Box
        sx={{
          flex: "1 1 0",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 1,
          display: "flex",
          maxHeight: "300px",
          overflowY: "auto",
        }}
      >
        {/* Table Header */}
        <Box
          sx={{
            alignSelf: "stretch",
            paddingLeft: 1,
            paddingRight: 1,
            paddingTop: 0.5,
            paddingBottom: 0.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 2,
            display: "flex",
            pr: 2, // Add right padding to align with row content
          }}
        >
          <Box
            sx={{
              flex: "1 1 0",
              paddingLeft: 1.5,
              color: theme.palette.text.primary,
              fontSize: 14,

              fontWeight: "700",
            }}
          >
            {t("campaigns.name") || "Nombre"}
          </Box>
          <Box
            sx={{
              width: 150,
              display: "flex",
              alignItems: "center",
              color: theme.palette.text.primary,
              fontSize: 14,

              fontWeight: "700",
              paddingLeft: 0.5, // Add padding to match row cell
            }}
          >
            {t("campaigns.platform") || "Plataforma"}
          </Box>
          <Box
            sx={{
              width: 120, // Increased width to match the action button container
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: theme.palette.text.primary,
              fontSize: 14,

              fontWeight: "700",
            }}
          >
            {t("common.actions") || "Acción"}
          </Box>
        </Box>

        {isLoadingUserCampaigns ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 4,
              width: "100%",
            }}
          >
            <CircularProgress />
          </Box>
        ) : userCampaignsError ? (
          <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: theme.palette.error.main,
              }}
            >
              {t("common.errorLoadingData") || "Error loading campaigns"}
            </Typography>
          </Box>
        ) : !userCampaignsData?.data?.campaigns ||
          userCampaignsData.data?.campaigns.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, width: "100%" }}>
            <Typography
              sx={{
                fontWeight: 600,
                color: theme.palette.grey[800],
              }}
            >
              {t("campaigns.noCampaignsFound") ||
                "No campaigns found for the selected platform"}
            </Typography>
          </Box>
        ) : (
          /* Table Rows */
          userCampaignsData.data?.campaigns?.flatMap((company: CompanyData) =>
            renderCompanyRows(company)
          )
        )}
      </Box>
    </>
  );
};

export default UserCampaignsTable;
